import PropTypes from "prop-types"
import React, { useState } from "react"

import Button from "src/components/Button"
import Modal from "src/components/Modal"

import { useReversePayment } from "../hooks"

const DEFAULT_REVERSAL_EXAMPLES = [
  "canceling the reservation or sale",
  "editing the dates or discounts",
  "removing the items being billed on this invoice",
]

const ReversePaymentConfirmationModal = ({
  isOpen,
  onClose,
  onReverseSuccess,
  reservationId,
  saleId,
  paymentId,
  examples = DEFAULT_REVERSAL_EXAMPLES,
}) => {
  const [error, setError] = useState(null)

  const { mutate: mutateReversePayment, isLoading: isReversing } =
    useReversePayment({
      reservationId,
      saleId,
      paymentId,
      options: {
        onSuccess: () => {
          setError(null)
          onReverseSuccess()
        },
        onError: (err) => {
          const baseMessage =
            err?.message ||
            "An error occurred while reversing the payment. Please try again"
          setError(
            `${baseMessage}. If the problem persists, please contact Dockwa at mayday@dockwa.com.`
          )
        },
      },
    })
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="default" stacked>
      <Modal.Header>
        <h1 className="m-0 text-lg font-semibold">Confirm Payment Reversal</h1>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-3 pb-4">
          <p>
            Please only proceed with reversing this payment if you intend to{" "}
            <strong>
              recharge this invoice using a different payment method.
            </strong>
          </p>
          <p>
            <strong>Do not</strong> proceed with this Payment Reversal if you
            plan to change what this invoice is billing — for example:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            {examples.map((example) => (
              <li key={example}>{example}</li>
            ))}
          </ul>
          <p>
            as that will create downstream issues with your accounting reports.
          </p>
          {error && (
            <div className="rounded-md flex items-start bg-red-50 p-3">
              <i className="icon icon-exclamation-circle mr-2 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-4">
          <Button variant="tertiary" onClick={onClose} disabled={isReversing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => mutateReversePayment()}
            isLoading={isReversing}
            disabled={isReversing}
          >
            Reverse payment now
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

ReversePaymentConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReverseSuccess: PropTypes.func.isRequired,
  reservationId: PropTypes.number,
  saleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentId: PropTypes.string,
  examples: PropTypes.arrayOf(PropTypes.string),
}

export default ReversePaymentConfirmationModal
